import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

const STATS = [
  { value: "2006", label: "Founded — and independent ever since" },
  { value: "30+", label: "Years of Todd's strategy experience" },
  { value: "15+", label: "Years — longest client relationships" },
  { value: "04", label: "Senior specialists, zero hand-offs" },
];

const APPROACH = [
  { label: "Search Engine Optimization", to: "/seo" },
  { label: "Website Optimization", to: "/website-optimization" },
  { label: "Google Ads Management", to: "/ad-remarketing" },
  { label: "Marketing Strategy & Consulting", to: "/coaching-consulting" },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };

  return (
    <main>
      <Seo
        title="About ZD Design — A Search Studio for the Long Game"
        description="ZD Design is a Kansas City boutique marketing agency founded in 2006 — a small senior team helping home-service companies and small businesses generate leads."
        path="/about"
      />

      <section className="grain relative overflow-hidden pb-16 pt-40 md:pb-24 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid items-start gap-12 lg:grid-cols-[1fr_400px] lg:gap-20">
            <FadeUp>
              <Eyebrow>About — founded 2006</Eyebrow>
              <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
                A small studio with a{" "}
                <span className="text-flame">long game.</span>
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
                ZD Design is a Kansas City boutique agency built on a
                traditional publishing and design foundation. Since 2006,
                we've brought decades of marketing experience to long-term
                client relationships — a small senior team whose only job is
                helping your business generate more leads.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div
                data-testid="about-staff-photo"
                className="group relative overflow-hidden rounded-2xl border border-slate-200"
              >
                <img
                  src="/img/staff-office.jpg"
                  alt="The ZD Design studio team at work in the office"
                  className="aspect-[4/3] w-full scale-105 object-cover grayscale transition-all duration-700 group-hover:scale-100 group-hover:grayscale-0"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="glass absolute bottom-5 left-5 rounded-xl border border-slate-200 px-5 py-4">
                  <div className="font-display text-xl font-bold text-slate-900">
                    The studio at work
                  </div>
                  <div className="font-mono text-base uppercase tracking-[0.22em] text-flame">
                    Kansas City, MO
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.15}>
            <div className="mt-14 grid grid-cols-2 gap-6 border-t border-slate-200 pt-8 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">
                    {s.value}
                  </div>
                  <div className="mt-2 font-mono text-base uppercase tracking-[0.15em] text-faint">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
            <FadeUp>
              <Eyebrow>Who we are</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Integrated marketing, done personally.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-mist">
                ZD Design is an integrated marketing agency specializing in
                search engine marketing, social media marketing, and marketing
                optimization. Our experienced team puts SEO to work alongside
                website optimization, a strong social media plan, creative
                content, and plenty of strategy.
              </p>
              <p className="mt-5 text-lg leading-relaxed text-mist">
                The result is a campaign that doesn't just deliver visibility —
                it gets you noticed, and keeps you chosen. From website
                retargeting to SEO to social advertising, we implement proven
                approaches that gain sales, backed by business growth
                strategies you'll use for years.
              </p>
              <ul className="mt-8 space-y-3 text-base text-slate-800">
                {[
                  "Small and medium-sized businesses are our specialty",
                  "The full package, from the ground up",
                  "One accountable team across SEO, SEM and social",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-flame" />
                    {t}
                  </li>
                ))}
              </ul>
            </FadeUp>

            <div className="space-y-5 self-center">
              <FadeUp delay={0.1}>
                <div
                  data-testid="about-mission"
                  className="glass rounded-2xl border border-slate-200 p-8"
                >
                  <Eyebrow>Mission</Eyebrow>
                  <p className="mt-4 text-lg leading-relaxed text-slate-700">
                    Build strong business relationships and provide online
                    marketing optimization for businesses that need the full
                    package — from the ground up.
                  </p>
                </div>
              </FadeUp>
              <FadeUp delay={0.18}>
                <div
                  data-testid="about-purpose"
                  className="glass rounded-2xl border border-slate-200 p-8"
                >
                  <Eyebrow>Purpose</Eyebrow>
                  <p className="mt-4 text-lg leading-relaxed text-slate-700">
                    Give companies the ideas and strategies to launch — and
                    keep growing — in the online arena. We optimize SEO, social
                    media, and SEM campaigns for better ROI.
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <FadeUp>
              <div className="group relative overflow-hidden rounded-2xl border border-slate-200">
                <img
                  src="/img/todd.webp"
                  alt="Todd, Chief Strategist at ZD Design Agency"
                  className="aspect-[4/5] w-full scale-105 object-cover grayscale transition-all duration-700 group-hover:scale-100 group-hover:grayscale-0"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="glass absolute bottom-5 left-5 rounded-xl border border-slate-200 px-5 py-4">
                  <div className="font-display text-xl font-bold text-slate-900">
                    Todd
                  </div>
                  <div className="font-mono text-base uppercase tracking-[0.22em] text-flame">
                    Chief Strategist
                  </div>
                </div>
              </div>
            </FadeUp>

            <div>
              <FadeUp>
                <Eyebrow>Meet Todd</Eyebrow>
                <h2 className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                  Thirty years of making brands{" "}
                  <span className="text-flame">felt, not just heard.</span>
                </h2>
              </FadeUp>
              <FadeUp delay={0.1}>
                <p className="mt-6 text-lg leading-relaxed text-mist">
                  Todd is a digital brand strategist with more than 30 years of
                  experience helping brands lead with purpose and
                  human-centric marketing. His gift is simplifying the complex:
                  crafting innovative strategies that make a company's voice
                  not just heard, but truly felt.
                </p>
                <p className="mt-5 text-lg leading-relaxed text-mist">
                  From startups to established names, he has guided businesses
                  in every industry — including the so-called unsexy ones, like
                  manufacturing and disaster restoration — to find their market
                  and connect with the right buyers. Yes, he can make a tractor
                  compelling.
                </p>
                <p className="mt-5 text-lg leading-relaxed text-mist">
                  Beyond marketing, Todd is an artist at heart — a singer,
                  songwriter, musician, and painter, dedicated to creativity in
                  all its forms.
                </p>
              </FadeUp>
              <FadeUp delay={0.18}>
                <blockquote className="mt-8 border-l-2 border-flame/50 pl-5 text-lg italic leading-relaxed text-slate-700">
                  "My passion lies in simplifying the complex and crafting
                  strategies that make your voice not just heard — but truly
                  felt."
                  <span className="mt-2 block font-mono text-base not-italic text-faint">
                    — Todd, Chief Strategist
                  </span>
                </blockquote>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>The full package</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              More than digital marketing.
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-mist">
              Every lever works better when it's pulled in concert. Explore
              what we do — then put it all behind one plan.
            </p>
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="mt-10 flex flex-wrap gap-3">
              {APPROACH.map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  data-testid={`about-service-link-${a.to.split("/").pop()}`}
                  className="group flex items-center gap-2 rounded-2xl border border-slate-200 bg-coal/70 px-5 py-3 text-base text-slate-800 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/50 hover:text-flame"
                >
                  {a.label}
                  <ArrowUpRight className="h-3.5 w-3.5 text-flame transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="mt-16 rounded-2xl border border-slate-200 bg-obsidian p-10 text-center md:p-14">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Ready to grow with a studio that sticks around?
              </h2>
              <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                Book a free strategy call — pick a time that suits you and talk
                directly with Todd.
              </p>
              <button
                data-testid="about-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free strategy call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </FadeUp>
        </div>
      </section>

      <section
        data-testid="about-photo-strip"
        className="border-t border-slate-200 py-20 md:py-24"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Inside the studio</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              A small team, deep in the work.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3 md:gap-6">
              {[
                {
                  src: "/img/studio-2.jpg",
                  alt: "The ZD Design team collaborating around the table in the studio",
                },
                {
                  src: "/img/studio-3.jpg",
                  alt: "Campaign analytics dashboard review in progress",
                },
                {
                  src: "/img/studio-4.jpg",
                  alt: "Reviewing performance charts on a tablet",
                },
              ].map((img) => (
                <div
                  key={img.src}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="aspect-[4/3] w-full scale-105 object-cover grayscale transition-all duration-700 group-hover:scale-100 group-hover:grayscale-0"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
